import { useEffect, useMemo, useState } from "react";
import { Calendar, Download, Loader2, Syringe } from "lucide-react";
import { usePatientAuth } from "../../store/authPatientStore";
import PatientPageHeading from "../../components/organisms/patient-dashboard/PatientPageHeading";
import { downloadFileFromUrl } from "../../lib/utils";
import {
  getPatientVaccinations,
  getVaccinationCertificatePdf,
  type PatientVaccination,
} from "../../api/authPatient";

interface VaccineRow {
  key: string;
  date: string;
  label: string;
  dose?: string;
  prochaineDose?: string | null;
}

export default function PatientVaccinations() {
  const { patient, activeProfil } = usePatientAuth();
  const [vaccinations, setVaccinations] = useState<PatientVaccination[]>([]);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState("");
  const patientNip = activeProfil?.nip?.trim() || patient?.nip?.trim() || "";

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!patientNip) {
        if (!mounted) return;
        setVaccinations([]);
        setError("NIP patient introuvable. Impossible de charger les vaccinations.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      const result = await getPatientVaccinations(patientNip);
      if (!mounted) return;

      if (!result.ok) {
        setVaccinations([]);
        setError(result.message || "Impossible de charger les vaccinations.");
        setLoading(false);
        return;
      }

      setVaccinations(result.data);
      setLoading(false);
    }

    void load();
    return () => {
      mounted = false;
    };
  }, [patientNip]);

  const fullName = useMemo(
    () => `${patient?.nom ?? ""} ${patient?.prenom ?? ""}`.trim(),
    [patient?.nom, patient?.prenom]
  );

  const formatDate = (value?: string) => {
    const source = value?.trim();
    if (!source) return "Date non renseignee";
    const parsed = new Date(source);
    if (Number.isNaN(parsed.getTime())) return source;
    return new Intl.DateTimeFormat("fr-FR", { dateStyle: "long", timeStyle: "short" }).format(parsed);
  };

  const getVaccinationLabel = (item: PatientVaccination) =>
    item.vaccin || item.libelleVaccin || item.typeVaccin || "Vaccin non renseigne";

  const rows: VaccineRow[] = useMemo(
    () =>
      vaccinations.map((item, idx) => ({
        key: item.id ?? `${item.dateVaccination ?? item.dateAdministration ?? idx}-${idx}`,
        date: formatDate(item.dateVaccination || item.dateAdministration),
        label: getVaccinationLabel(item),
        dose: item.dose,
        prochaineDose: item.prochaineDose ? formatDate(item.prochaineDose) : null,
      })),
    [vaccinations]
  );

  const downloadCarnet = async () => {
    if (!patientNip || pdfLoading) return;
    setPdfLoading(true);
    setError("");
    const result = await getVaccinationCertificatePdf(patientNip);
    setPdfLoading(false);
    if (!result.ok) {
      setError(result.message || "Impossible de générer le carnet PDF.");
      return;
    }
    await downloadFileFromUrl(result.pdfUrl, `carnet-vaccination-${patientNip}.pdf`);
  };

  return (
    <div className="space-y-6">
      <PatientPageHeading
        title="Carnet de vaccination"
        description={fullName ? `Historique vaccinal de ${fullName}` : "Suivi de vos vaccins"}
      />

      <div className="rounded-[12px] border bg-white p-5 md:p-6" style={{ borderColor: "#E5E7EB" }}>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => void downloadCarnet()}
            className="inline-flex h-10 items-center gap-1.5 rounded-[10px] border px-4 text-sm font-semibold whitespace-nowrap disabled:opacity-50"
            style={{ borderColor: "#00553E", color: "#00553E", background: "white" }}
            disabled={loading || pdfLoading || rows.length === 0 || !patientNip}
          >
            {pdfLoading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {pdfLoading ? "Génération…" : "Télécharger (PDF)"}
          </button>
        </div>

        {error && (
          <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2">{error}</div>
        )}

        <div className="mt-6">
          {loading && <p className="text-sm text-gray-500">Chargement des vaccinations…</p>}
          {!loading && !error && rows.length === 0 && (
            <div className="text-sm text-gray-600 bg-gray-50 border rounded-[10px] px-3 py-2" style={{ borderColor: "#E5E7EB" }}>
              Aucune vaccination disponible pour le moment.
            </div>
          )}
          {!loading && !error && rows.length > 0 && (
            <div className="space-y-3">
              {rows.map((row) => (
                <div
                  key={row.key}
                  className="rounded-[10px] border bg-[#FAFBFB] p-4 md:p-5"
                  style={{ borderColor: "#E5E7EB" }}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] bg-[#DAF1E9]">
                        <Syringe size={18} className="text-[#00553E]" />
                      </span>
                      <div>
                        <p className="text-sm font-semibold capitalize text-[#0B1F16]">{row.label}</p>
                        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500">
                          <Calendar size={12} />
                          {row.date}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {row.dose && (
                        <span className="inline-flex items-center rounded-full bg-[#EEF1F1] px-2.5 py-1 text-xs font-semibold text-[#0B1F16]">
                          Dose {row.dose}
                        </span>
                      )}
                      {row.prochaineDose && (
                        <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-900">
                          Prochaine dose : {row.prochaineDose}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

